// AddressWithMap.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import AddressBarComponent from '../AddressBar/AddressBarComponent';
import StaticMapCard from '../StaticMapCard/StaticMapCardComponent';
import AvailabilityBubble from '../AvailabilityBubble/AvailabilityBubbleComponent';
import { getFibreOffers } from '../../api/apiServices';

// Google geocode results describe an address as a flat list of typed
// components rather than named fields, so pull out the pieces the fibre
// accept API needs individually.
function parseAddressComponents(components = []) {
  const get = (type) =>
    components.find((c) => c.types?.includes(type))?.long_name || '';

  const streetNumber = get('street_number');
  const route = get('route');

  return {
    streetName: [streetNumber, route].filter(Boolean).join(' '),
    suburb: get('sublocality_level_1') || get('sublocality') || '',
    town: get('locality') || '',
    postalCode: get('postal_code') || '',
    unitNumber: get('subpremise') || '',
    complexName: get('premise') || '',
  };
}

export default function AddressWithMap({
  name = 'address',
  question,
  values,
  onChange,
  onNext,
  registerValidation,
  setValidationState,
  isMobile,
  inputRef: externalInputRef,
  nextFieldRef,
}) {
  const [coords, setCoords] = useState(null);
  const [addressLine, setAddressLine] = useState('');
  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'unavailable' | 'error' | null

  const inputRef = externalInputRef || useRef(null);

  const mockMode = question?.mockMode; // 'positive' | 'negative'

  const checkAvailability = useCallback(
    async ({ lat, lng }) => {
      if (mockMode) {
        setStatus('loading');
        await new Promise((r) => setTimeout(r, 500));
        setStatus(mockMode === 'positive' ? 'success' : 'unavailable');
        return;
      }

      try {
        setStatus('loading');

        // Build minimum payload your service expects
        const formValues = {
          latitude: lat,
          longitude: lng,
        };

        const data = await getFibreOffers(formValues);

        // ✅ Check for Fibre availability
        const hasFibre = data?.networkTypeOptions?.some(
          (item) => item.label === 'Fibre'
        );

        if (data?.wasSuccessful === true && hasFibre) {
          onChange?.('fibreAvailability', {
            wasSuccessful: data?.wasSuccessful === true,
            hasFibre: !!hasFibre,
          });

          setStatus('success');
        } else {
          setStatus('unavailable');
        }
      } catch (err) {
        setStatus('error');
      }
    },
    [mockMode]
  );

  /**
   * Register validation that ONLY passes when the API returned a positive result.
   * This will be triggered when the main CTA runs validations,
   * and we can also call it from our guarded onNext below.
   */
  useEffect(() => {
    registerValidation?.(name, () => {
      const isValid = status === 'success';
      setValidationState?.(name, isValid);
      return isValid;
    });
  }, [name, status, registerValidation, setValidationState]);

  /**
   * Guard auto-advance coming from AddressBarComponent on selection/Enter.
   * If API has not confirmed success yet, block advancing and set invalid state.
   */
  const guardedOnNext = useCallback(() => {
    const ok = status === 'success';
    setValidationState?.(name, ok);
    if (ok) onNext?.();
    // If not ok, do nothing — user stays on this step
  }, [name, onNext, setValidationState, status]);

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Centered Address Bar */}
      <Box
        sx={{
          width: isMobile ? '92vw' : '730px',
          display: 'flex',
          justifyContent: 'center',
          mx: 'auto',
        }}
      >
        <AddressBarComponent
          name={name}
          question={{ ...question }}
          values={values}
          onChange={(field, val, ...rest) => {
            if (field === name) {
              setCoords(null);
              setAddressLine('');
              setStatus(null);
              // ✅ IMPORTANT: clear previous fibre result in QuestionEngine state
              onChange?.('fibreAvailability', {
                wasSuccessful: false,
                hasFibre: false,
              });
            }
            onChange?.(field, val, ...rest);
          }}
          onNext={guardedOnNext} // replace raw onNext to prevent premature advance
          setValidationState={setValidationState}
          registerValidation={registerValidation}
          inputRef={inputRef}
          nextFieldRef={nextFieldRef}
          isMobile={isMobile}
          onLocationResolved={(ll, formatted, place) => {
            setCoords(ll);
            setAddressLine(formatted || '');
            setStatus(null);

            onChange?.('latitude', ll?.lat ?? '');
            onChange?.('longitude', ll?.lng ?? '');
            const {
              streetName,
              suburb,
              town,
              postalCode,
              unitNumber,
              complexName,
            } = parseAddressComponents(place?.address_components);
            onChange?.('streetName', streetName);
            onChange?.('suburb', suburb);
            onChange?.('town', town);
            onChange?.('postalCode', postalCode);
            onChange?.('unitNumber', unitNumber);
            onChange?.('complexName', complexName);

            checkAvailability(ll, formatted);
          }}
        />
      </Box>

      {/* Map (square) appears below */}
      {coords && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'center',
            width: isMobile ? '92vw' : '730px',
            mx: 'auto',
          }}
        >
          <StaticMapCard
            coords={coords}
            width={isMobile ? '80vw' : 730}
            height={isMobile ? '220px' : 267}
            borderRadius={isMobile ? 6 : 12}
            overlay={
              status && status !== 'loading' ? (
                <AvailabilityBubble
                  variant={status}
                  addressLine={addressLine}
                  avatarSrc={HippoAvatar}
                  tailOffset={-24}
                  isMobile={isMobile}
                />
              ) : null
            }
          />
        </Box>
      )}
    </Box>
  );
}
